import { converseApi } from '@/features/messaging';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MessageCircle, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ChatPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['converses'],
    queryFn: () => converseApi.getConverses(1, 50),
  });

  const filteredConverses = data?.items?.filter((converse) =>
    converse.participantName?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Messages</h1>
        <p className="text-muted-foreground mt-1">Échangez avec vos connexions</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher une conversation..."
          className="pl-10 h-11"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Conversations List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-14 w-14 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredConverses && filteredConverses.length > 0 ? (
        <div className="space-y-3">
          {filteredConverses.map((converse) => (
            <Link key={converse.id} to={`/chat/${converse.id}`}>
              <Card className="cursor-pointer border-border hover:border-primary/30 hover:shadow-md transition-all duration-300 group">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Avatar className="h-14 w-14 ring-2 ring-border group-hover:ring-primary/30 transition-all duration-300">
                        <AvatarImage
                          src={converse.participantAvatar}
                          alt={converse.participantName}
                        />
                        <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-lg">
                          {converse.participantName?.charAt(0).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      {/* Online indicator */}
                      {converse.participantOnline && (
                        <div className="absolute bottom-0 right-0 h-4 w-4 bg-success rounded-full border-2 border-card" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-semibold truncate">{converse.participantName}</h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(converse.unreadCount ?? 0) > 0 && (
                            <Badge className="bg-primary text-primary-foreground h-5 min-w-5 px-1.5 text-xs font-medium">
                              {(converse.unreadCount ?? 0) > 9 ? '9+' : converse.unreadCount}
                            </Badge>
                          )}
                          {converse.lastMessageAt && (
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(converse.lastMessageAt), {
                                addSuffix: true,
                                locale: fr,
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                      <p
                        className={`text-sm truncate mt-1 ${
                          (converse.unreadCount ?? 0) > 0
                            ? 'text-foreground font-medium'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {converse.lastMessage || 'Aucun message pour le moment'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="h-16 w-16 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-4">
            <MessageCircle className="h-8 w-8 text-primary" />
          </div>
          <h2 className="font-display text-xl font-semibold mb-2">Aucune conversation</h2>
          <p className="text-muted-foreground max-w-sm mx-auto">
            Commencez à échanger avec vos connexions pour voir vos conversations ici
          </p>
        </div>
      )}
    </div>
  );
}
